import { Component, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { RestService } from '../../../services/rest/rest.service';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { HelpIndex, Endpoint } from '../../../models/enums';
import { XMLService, SearchWrapper, SearchConfig } from '../../../services/xml/xml.service';
import { ToggleItem } from '../../../models/toggle-item';
import { CommonService } from '../../../services/common/common.service';
import { TargetPickerModalComponent } from '../target-picker-modal/target-picker-modal.component';
import { TargetPickerTreeType } from '../../target-picker-tree/target-picker-tree.component';
import { WorkflowStep } from '../../../models/workflow';
import { GenericInputComponent } from '../../generic-input/generic-input.component';


@Component({
    standalone: false,
    selector: 'res-run-combined-search-modal',
    templateUrl: './run-combined-search-modal.component.html',
    styleUrls: ['./run-combined-search-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

// For run as combined from bulk search report set searchTargetHandle and searchOptions in modalData
export class RunCombinedSearchModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild('queryInput') queryInput: GenericInputComponent;

    modalAutomationId = 'run-as-combined-search';
    modalClass = 'run-combined-search-modal';
    modalHelpIndex = HelpIndex.myedisc_run_combined_search_dlg;
    queryString = '';
    operationType = 'OR';
    queryEdited = false;
    showTargetSelector = true;
    isBusy = false;
    isBusyMessage = 'Searching ...';
    searchTarget = null;
    searchOptions = [
        new ToggleItem({
            label: 'Include Families',
            isChecked: true,
        }),
        new ToggleItem({
            label: 'Include Metadata',
            isChecked: true,
        }),
        new ToggleItem({
            label: 'Expand Synonyms',
        }),
        new ToggleItem({
            label: 'Generate Reports',
        })
    ];
    corporaFilters: Array<any> = [];
    corporaFilterTooltip = '';
    valueOnInit = '';

    constructor(private modalService: BsModalService, private modalRef: BsModalRef, private commonService: CommonService,
        private restService: RestService, private xmlService: XMLService) {
        super(modalService, modalRef);
    }

    ngOnInit() {
        if (this.modalData) {
            if (this.modalData.queries) {
                this.queryString = this.modalData.queries.join(` ${this.operationType} `);
                if (this.queryString) {
                    setTimeout(() => { this.queryInput?.colorize(); });
                }
            }
            if (this.modalData.searchTargetHandle) {
                this.showTargetSelector = false;
                this.modalClass += ' bulk-search-mode';
            } else {
                this.searchTarget = { name: 'Project Data', handle: this.commonService.projectData.handle };
                this.modalClass += ' search-history-mode';
            }
        }
    }

    setInput(): void {
        if (((window as any).hljs == null && !this.commonService.currentBrowserIsIE) || !this.queryInput) return;
        this.queryInput?.setText(this.queryString);
    }

    keyPressEvent(event) {
        this.queryEdited = true;
        this.operationType = null;
    }

    inputChanged(event?: any): void {
        setTimeout(() => {
            if (event?.source === 'user') {
                const deleteClipBoardEventTriggered = event.delta?.ops[0]?.delete || event.delta?.ops[1]?.delete;
                if (/\s/.test(event.delta?.ops[1]?.insert) || /\s/.test(event.delta?.ops[0]?.insert) || deleteClipBoardEventTriggered) {
                    const range = this.queryInput?.getSelection();
                    if (deleteClipBoardEventTriggered && this.queryInput.getText().trim().length === 0) {
                        this.queryString = event.text.trim();
                    } else {
                        this.queryInput?.setText(this.queryInput.capitalizeKeywords(this.queryInput?.getText()));
                    }
                    this.queryInput?.setSelection(range);
                }
                this.queryInput?.colorize();
            }
            this.queryString = this.queryInput.getText();
        });
    }

    setCursorAtIndex() {
        setTimeout(() => {
            if (this.queryInput) {
                const rangeIndex = this.queryInput.getText().length;
                const range = { index: rangeIndex, length: 0 };
                this.queryInput.setSelection(range);
            }
        });
    }

    isPrimaryButtonEnabled(): boolean {
        const inputText = this.queryInput?.getText();
        if (this.showTargetSelector) {
            return inputText?.trim() !== '' && this.searchTarget && this.searchTarget.handle !== '' && !this.isBusy;
        }
        return inputText?.trim() !== '' && !this.isBusy;
    }

    setOperationType(operation: string): void {
        this.operationType = operation;
        this.queryString = this.modalData.queries.join(` ${this.operationType} `);
        this.setInput();
        this.queryInput?.colorize();
        this.setCursorAtIndex();
    }

    getReportRequests(): Array<any> {
        const reportsForSearch = [];
        reportsForSearch.push({ cachedOnly: false, reportType: 'OCR_CONFIDENCE_LEVEL' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'DATE_PRIMARY', count: 10 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'OWNER' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'TECHNICAL_REDUCTION' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'FILETYPE', attributes: [{ name: 'consolidated', value: 'true' }] });
        reportsForSearch.push({ cachedOnly: false, reportType: 'DOCUMENT_CLASS' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'CORPUS_SOURCE' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'LANGUAGE', count: 10 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'DOMINANT_LANGUAGE', count: 10 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'GROUP_VIEW_MEMBERSHIP', count: 10 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'TERM_SEARCH_HIT_COUNT' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'MAG_HIT_COUNT' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'SEARCH' });
        reportsForSearch.push({ cachedOnly: false, reportType: 'TAG_SUMMARY', count: 5 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'EMAIL_SENDER', count: 10 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'EMAIL_RECEIVER', count: 10 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'EMAIL_SENT_DOMAINS', count: 5 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'EMAIL_RECEIVED_DOMAINS', count: 5 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'DATE_EMAIL_RECEIVED', count: 5 });
        reportsForSearch.push({ cachedOnly: false, reportType: 'DATE_EMAIL_SENT', count: 5 });

        return reportsForSearch;
    }

    search(): void {
        if ((this.modalData && this.modalData.searchTargetHandle) || (this.searchTarget && this.searchTarget.handle)) {
            const searchWrapper = new SearchWrapper(new SearchConfig({ isFromCombinedSearch: true, isSimpleSearch: false, searchDialect: this.commonService.searchDialect }));
            const trimmedQueryText = this.queryInput.getText();
            const query = this.xmlService.replaceWithEscapeChar(trimmedQueryText, true);
            if (this.operationType === 'AND') {
                searchWrapper.allOfTheseWords.value = query;
            } else {
                searchWrapper.oneOrMoreWords.value = query;
            }
            const xmlQuery = searchWrapper.objectToXml().replace(/\u00a0/g, ' ').replace(/(&nbsp;)/gi, '');
            const targetHandle = this.showTargetSelector ? (this.searchTarget.view?.handle ? this.searchTarget.view.handle : this.searchTarget.handle) : this.modalData.searchTargetHandle;
            const searchRequest = {
                viewHandleList: [targetHandle],
                query: xmlQuery,
                includeFamilyMembers: this.modalData.searchOptions ? this.modalData.searchOptions.includeFamily : this.searchOptions[0].isChecked,
                expandFields: this.modalData.searchOptions ? this.modalData.searchOptions.includeMetadata : this.searchOptions[1].isChecked,
                includeSynonyms: this.modalData.searchOptions ? this.modalData.searchOptions.includeSynonyms : this.searchOptions[2].isChecked,
                taskDescription: 'Combined Search for ' + query,
                reportRequests: this.searchOptions[3].isChecked ? this.getReportRequests() : [],
                corporaFilters: this.corporaFilters && this.corporaFilters.length > 0 ? this.corporaFilters.map(elem => elem.handle) : []
            };
            if (!this.showTargetSelector) {
                delete searchRequest.reportRequests;
            }
            this.isBusy = true;
            this.restService.post(Endpoint.viewManager_search, searchRequest, true, true).then(() => {
                this.isBusy = false;
                this.closeModal();
            }, () => {
                this.isBusy = false;
            });
        }
    }

    checkHasScroll(): boolean {
        return this.queryInput?.hasScrollOverflow() ?? false;
    }

    onClearQuery(): void {
        this.queryString = '';
        this.setInput();
        this.queryEdited = true;
        this.operationType = null;
        this.setCursorAtIndex();
    }

    openTargetSelector(): void {
        this.commonService.openModal(TargetPickerModalComponent, {
            modalData: { targetPickerTreeType: TargetPickerTreeType.selectSearchTarget, dataSetFilter: this.corporaFilters },
            class: 'horizontal-px-700 vertical-percent-80'
        }).then(response => {
            if (response && response.modalResult && response.modalData && response.modalData.target) {
                this.searchTarget = response.modalData.target.data;
                this.corporaFilters = response.modalData.dataSetFilter;
                if (this.searchTarget instanceof WorkflowStep) {
                    response.modalData.target.data.name = response.modalData.target.data.description;
                }
                if (this.corporaFilters && this.corporaFilters.length > 0) {
                    const names = this.corporaFilters.map(function (elem) { return '- ' + elem.name; });
                    this.corporaFilterTooltip = 'Filtered by Data Set/s:\n' + names.join('\n');
                } else {
                    this.corporaFilterTooltip = '';
                }
            }
        });
    }
}